import { useShow } from '@refinedev/core';
import { Show } from '@refinedev/antd';
import { Card, Tag, message, Space, Button, Descriptions, Typography } from 'antd';
import { CheckOutlined, BulbOutlined, UserOutlined, PhoneOutlined, CalendarOutlined } from '@ant-design/icons';
import { apiClient } from '../../lib/api-client';
import { t } from '../../i18n';

const { Paragraph, Text } = Typography;

interface SuggestionMessage {
  id: number;
  content: string;
  createdAt: string;
}

interface Suggestion {
  id: number;
  subject: string;
  status: 'OPEN' | 'IN_PROGRESS' | 'RESOLVED' | 'CLOSED';
  createdAt: string;
  user: {
    fullName: string;
    phone: string;
  };
  messages: SuggestionMessage[];
}

const statusLabels: Record<string, string> = {
  OPEN: t('suggestions.statusNew'),
  IN_PROGRESS: t('suggestions.statusUnderReview'),
  RESOLVED: t('suggestions.statusAccepted'),
  CLOSED: t('suggestions.statusClosed'),
};

const statusColors: Record<string, string> = {
  OPEN: 'blue',
  IN_PROGRESS: 'orange',
  RESOLVED: 'green',
  CLOSED: 'default',
};

const parseCategory = (subject: string | undefined) => {
  if (!subject) return '-';
  const prefix = 'Taklif:';
  if (subject.startsWith(prefix)) return subject.slice(prefix.length).trim() || '-';
  return subject;
};

export const SuggestionShow = () => {
  const { queryResult } = useShow<Suggestion>();
  const { data, isLoading, refetch } = queryResult;
  const suggestion = data?.data;

  const handleAccept = async () => {
    try {
      await apiClient.put(`/admin/tickets/${suggestion?.id}/resolve`);
      message.success(t('suggestions.statusAccepted'));
      refetch();
    } catch {
      message.error(t('common.error'));
    }
  };

  // The actual suggestion text is the first (and usually only) message
  const content = suggestion?.messages?.[0]?.content || '';
  const category = parseCategory(suggestion?.subject);
  const isResolved = suggestion?.status === 'RESOLVED' || suggestion?.status === 'CLOSED';

  return (
    <Show
      isLoading={isLoading}
      title={
        <Space>
          <BulbOutlined style={{ color: '#F39C12' }} />
          <span>
            {t('suggestions.title')} #{suggestion?.id}
          </span>
        </Space>
      }
      headerButtons={
        !isResolved && (
          <Button type="primary" icon={<CheckOutlined />} onClick={handleAccept}>
            {t('suggestions.statusAccepted')}
          </Button>
        )
      }
    >
      <Card style={{ marginBottom: 24 }}>
        <Descriptions column={2} labelStyle={{ fontWeight: 600 }}>
          <Descriptions.Item label={t('suggestions.category')}>
            <Tag color="gold">{category}</Tag>
          </Descriptions.Item>
          <Descriptions.Item label={t('common.status')}>
            <Tag color={statusColors[suggestion?.status || 'OPEN']}>
              {statusLabels[suggestion?.status || 'OPEN']}
            </Tag>
          </Descriptions.Item>
          <Descriptions.Item
            label={
              <Space size={4}>
                <UserOutlined />
                {t('support.ticketUser')}
              </Space>
            }
          >
            {suggestion?.user?.fullName || t('common.noName')}
          </Descriptions.Item>
          <Descriptions.Item
            label={
              <Space size={4}>
                <PhoneOutlined />
                {t('common.phone')}
              </Space>
            }
          >
            {suggestion?.user?.phone || '-'}
          </Descriptions.Item>
          <Descriptions.Item
            label={
              <Space size={4}>
                <CalendarOutlined />
                {t('common.createdAt')}
              </Space>
            }
            span={2}
          >
            {suggestion?.createdAt
              ? new Date(suggestion.createdAt).toLocaleString('uz-UZ')
              : '-'}
          </Descriptions.Item>
        </Descriptions>
      </Card>

      <Card
        title={
          <Space>
            <BulbOutlined style={{ color: '#F39C12' }} />
            <span>{t('suggestions.content')}</span>
          </Space>
        }
        style={{
          background: '#FFFBF0',
          borderLeft: '4px solid #F39C12',
        }}
      >
        {content ? (
          <Paragraph
            style={{
              fontSize: 15,
              lineHeight: 1.7,
              color: '#2C3E50',
              marginBottom: 0,
              whiteSpace: 'pre-wrap',
            }}
          >
            {content}
          </Paragraph>
        ) : (
          <Text type="secondary">{t('common.noData')}</Text>
        )}
      </Card>
    </Show>
  );
};
